// src/components/RainbowBorder.tsx
import React, { useEffect, useRef } from "react";
import { View, ViewStyle, StyleSheet, Animated, Text } from "react-native";

// Rainbow colors array for reuse
const RAINBOW_COLORS = [
  "#FF5757", // Red
  "#FF914D", // Orange
  "#FFDE59", // Yellow
  "#7ED957", // Green
  "#5CE1E6", // Blue
  "#7B5CFF", // Indigo
  "#C879FF", // Violet
];

interface RainbowBorderProps {
  children: React.ReactNode;
  isActive?: boolean;
  borderWidth?: number;
  borderRadius?: number;
  offset?: number;
  style?: ViewStyle;
  zIndex?: number;
}

/**
 * A component that adds a rainbow border animation around its children
 */
const RainbowBorder: React.FC<RainbowBorderProps> = ({
  children,
  isActive = false,
  borderWidth = 3,
  borderRadius = 5,
  offset = 4,
  style,
  zIndex = -1,
}) => {
  const colorAnimation = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (isActive) {
      Animated.loop(
        Animated.timing(colorAnimation, {
          toValue: RAINBOW_COLORS.length,
          duration: 3000,
          useNativeDriver: false,
        }),
      ).start();
    } else {
      colorAnimation.setValue(0);
    }

    return () => {
      colorAnimation.stopAnimation();
    };
  }, [isActive, colorAnimation]);

  const borderColor = colorAnimation.interpolate({
    inputRange: [
      ...RAINBOW_COLORS.map((_, index) => index),
      RAINBOW_COLORS.length,
    ],
    outputRange: [...RAINBOW_COLORS, RAINBOW_COLORS[0]],
  });

  const sanitizedChildren = React.Children.map(children, (child, index) => {
    if (typeof child === "string") {
      return <Text key={`rainbow-border-text-${index}`}>{child}</Text>;
    }
    return child;
  });

  return (
    <View style={[styles.container, style]}>
      {isActive && (
        <Animated.View
          style={{
            position: "absolute",
            top: -offset,
            left: -offset,
            right: -offset,
            bottom: -offset,
            borderWidth,
            borderRadius,
            borderColor,
            pointerEvents: "none", // to not mess with template editing touch events
            zIndex: zIndex,
            shadowColor: "#FF914D",
            shadowOffset: { width: 0, height: 0 },
            shadowOpacity: 0.8,
            shadowRadius: 10,
            elevation: 8,
          }}
        />
      )}
      {sanitizedChildren}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: "relative",
  },
});

export default RainbowBorder;
